<template>
    <div>
    <table class="w-full">
        <tbody class="striped">
            <tr class="not_striped">
                <th :colspan="invoiceDataColumnCount" class="pt-6 text-left text-2xl text-gray-dark font-bold" :class="{'pb-2': !order.allow_substitutions}">
                    Order Details
                </th>
            </tr>
            <tr v-if="isAdmin || type != 'invoice'" class="not_striped">
                <th :colspan="invoiceDataColumnCount" class="pb-2 text-left">
                    <div class="font-bold text-gray-dark text-lg">
                        SUBSTITUTIONS<span v-if="!order.allow_substitutions"> NOT</span> ALLOWED
                    </div>
                </th>
            </tr>
            <tr class="not_striped border-b border-gray">
                <th class="py-1 text-left px-2 font-bold">Qty</th>
                <th class="py-1 text-left pl-4 pr-2 w-1/5">UofM</th>
                <th class="py-1 text-left px-2">Item</th>
                <th class="py-1 text-center px-2">Zone</th>
                <th v-if="showPricing" class="py-1 px-2 text-center">Price</th>
                <th v-if="showPricing && hasPromo(order)" class="py-1 px-2 text-right whitespace-nowrap">Discount</th>
                <th v-if="showPricing" class="py-1 px-2 text-right whitespace-nowrap">Total</th>
                <th v-if="showHSTColumn" class="py-1 text-center px-1 w-10 max-w-[2.5rem] font-bold">HST</th>
            </tr>
            <template v-for="(item, index) in orderItemsSortedByZone(order.order_items)" :key="index">
                <!-- Parent Item -->
                <tr
                    class="border-b border-gray"
                    :class="{
                        'text-gray-500': type != 'invoice' && orderItemsInlineSubitems(item).length > 0,
                    }"
                    :style="{
                        'border-bottom': type != 'invoice' && orderItemsInlineSubitems(item).length == 0 && isLastItemInZone(index) ? '2px solid #AAA' : null,
                    }"
                    >
                    <td class="text-center px-2 font-bold">{{ item.quantity }}</td>
                    <td v-if="type == 'outlet_order'" class="text-left whitespace-nowrap pl-4 pr-2"><span v-html="boldProductHighlights(item.description, item.product.picksheet_highlights)"/></td>
                    <td v-else class="text-left whitespace-nowrap pl-4 pr-2"><span v-html="boldProductHighlights(item.size, item.product.picksheet_highlights)"/></td>
                    <td v-if="type == 'outlet_order'" class="text-left px-2">
                        <span v-html="boldProductHighlights(item.title, item.product.picksheet_highlights)"/>
                        <span v-if="item.description_alt && !item.description_alt.match(/\n/g)"> - <span v-html="boldProductHighlights(item.description_alt, item.product.picksheet_highlights)"/></span>
                        <div v-if="item.description_alt && item.description_alt.match(/\n/g)" class="text-xs italic"><span v-html="boldProductHighlights(item.description_alt, item.product.picksheet_highlights)"/></div>
                        <div v-if="item.contents && shown_subitems(item).length == 0" class="text-xs italic"><span v-html="boldProductHighlights(item.contents, item.product.picksheet_highlights)"/></div>
                    </td>
                    <td v-else class="text-left px-2">
                        <span v-html="boldProductHighlights(item.title, item.product.picksheet_highlights)"/>
                        <span v-if="item.description"> - <span v-html="boldProductHighlights(item.description, item.product.picksheet_highlights)"/></span>
                        <div v-show="item.product_notes" class="text-xs italic"><span v-html="boldProductHighlights(item.product_notes, item.product.picksheet_highlights)"/></div>
                    </td>
                    <td v-if="type == 'outlet_order'" class="text-center px-2">
                        <span v-if="item.product && !item.product.category.subitems_on_separate_picksheet">{{ item.product.retail_zone }}</span>
                    </td>
                    <td v-else-if="order.customer?.type == 'School'" class="text-center px-2">
                        <span v-if="item.product && !item.product.category.subitems_on_separate_picksheet">{{ item.product.school_zone }}</span>
                    </td>
                    <td v-else class="text-center px-2">
                        <span v-if="item.product && !item.product.category.subitems_on_separate_picksheet">{{ item.product.zone }}</span>
                    </td>
                    <td v-if="showPricing" class="text-right px-2">{{ toCurrency(item.price) }}</td>
                    <td v-if="showPricing && hasPromo(order)" class="text-right px-2">
                        {{ lineDiscountAmt(order, item) > 0 ? toCurrency(lineDiscountAmt(order, item)) : '' }}
                    </td>
                    <td v-if="showPricing" class="text-right px-2">
                        {{ toCurrency(getLineDiscountedTotal(order, item)) }}
                    </td>
                    <td v-if="showHSTColumn" class="text-center px-1 w-10 max-w-[2.5rem] font-bold whitespace-nowrap">
                        {{ itemHSTChargeMarker(item) }}
                    </td>
                </tr>
                <!-- Inline Subitems -->
                <template v-if="type != 'invoice'">
                <tr  v-for="(subitem, si_index) in orderItemsInlineSubitems(item)" class="border-b border-gray" :key="index + '_' + si_index + '_subitem'">
                    <td class="combo_with_integrated_subitems text-center px-2 font-bold">{{ subitem.quantity }}</td>
                    <td v-if="type == 'outlet_order'" class="combo_with_integrated_subitems text-left whitespace-nowrap pl-4 pr-2"><span v-html="boldProductHighlights(subitem.description, subitem.product.picksheet_highlights)"/></td>
                    <td v-else class="combo_with_integrated_subitems text-left whitespace-nowrap pl-4 pr-2"><span v-html="boldProductHighlights(subitem.size, subitem.product.picksheet_highlights)"/></td>
                    <td v-if="type == 'outlet_order'" class="combo_with_integrated_subitems text-left px-2">
                        <span v-html="boldProductHighlights(subitem.title, subitem.product.picksheet_highlights)"/>
                        <span v-if="subitem.description && subitem.contents"> - <span v-html="boldProductHighlights(subitem.description)"/></span>
                        <span v-if="subitem.description_alt && !subitem.description_alt.match(/\n/g)"> - <span v-html="boldProductHighlights(subitem.description_alt, subitem.product.picksheet_highlights)"/></span>
                        <span v-if="subitem.description && !subitem.contents"> - <span v-html="boldProductHighlights(subitem.description, item.product.picksheet_highlights)"/></span>
                        <div v-if="subitem.description_alt && subitem.description_alt.match(/\n/g)" class="text-xs italic"><span v-html="boldProductHighlights(subitem.description_alt, item.product.picksheet_highlights)"/></div>
                        <div v-if="subitem.contents" class="text-xs italic"><span v-html="boldProductHighlights(subitem.contents, subitem.product.picksheet_highlights)"/></div>
                    </td>
                    <td v-else class="combo_with_integrated_subitems text-left px-2">
                        <span v-html="boldProductHighlights(subitem.title, subitem.product.picksheet_highlights)"/>
                        <span v-if="subitem.description"> - <span v-html="boldProductHighlights(subitem.description, subitem.product.picksheet_highlights)"/></span>
                        <div v-show="subitem.product_notes" class="text-xs italic"><span v-html="boldProductHighlights(subitem.product_notes, subitem.product.picksheet_highlights)"/></div>
                    </td>
                    <td v-if="type == 'outlet_order'" class="combo_with_integrated_subitems text-center px-2">
                        {{ subitem.product.retail_zone }}
                    </td>
                    <td v-else-if="order.customer?.type == 'School'" class="combo_with_integrated_subitems text-center px-2">
                        {{ subitem.product.school_zone }}
                    </td>
                    <td v-else class="combo_with_integrated_subitems text-center px-2">
                        {{ subitem.product.zone }}
                    </td>
                </tr>
                </template>
            </template>
            <tr class="not_striped">
                <td class="text-center px-2 font-bold">{{ order.item_count_picksheet }}</td>
                <td rowspan="6" style="border-bottom:none;" class="align-top">
                    <div v-if="type == 'outlet_order'" class="text-center">
                        <img src="/images/quality-guaranteed.png" alt="quality" id="quality" style="width:100%;max-width:300px;" />
                    </div>
                </td>
                <th v-if="showPricing" :colspan="totalsSectionLabelColspan - 2" class="text-right px-2">Subtotal:</th>
                <td v-if="showPricing" class="text-right px-2">{{ toCurrency(subtotal(order)) }}</td>
                <td v-if="showHSTColumn && showPricing" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            
            <template v-if="showPricing">
            <tr v-if="delivery_fee(order) > 0" class="not_striped">
                <th :colspan="totalsSectionLabelColspan" class="text-right px-2">Delivery Fee:</th>
                <td class="text-right px-2">{{ toCurrency(delivery_fee(order)) }}</td>
                <td v-if="showHSTColumn" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            <tr v-if="tax(order) > 0 || tax(order) < 0" class="not_striped">
                <th :colspan="totalsSectionLabelColspan" class="text-right px-2">{{ taxRowLabel }}</th>
                <td class="text-right px-2">{{ toCurrency(tax(order)) }}</td>
                <td v-if="showHSTColumn" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            <tr v-if="type == 'outlet_order' && order.promo_code_amount > 0" class="not_striped">
                <th :colspan="totalsSectionLabelColspan" class="text-right px-2">Promo Code Discount:</th>
                <td class="text-right px-2">-{{ toCurrency(parseFloat(order.promo_code_amount)) }}</td>
                <td v-if="showHSTColumn" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            <tr v-if="type != 'outlet_order' && order.applied_credit_amount > 0" class="not_striped">
                <th :colspan="totalsSectionLabelColspan" class="text-right px-2">Prepaid:</th>
                <td class="text-right px-2">{{ toCurrency(parseFloat(order.applied_credit_amount)) }}</td>
                <td v-if="showHSTColumn" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            <tr class="not_striped">
                <th :colspan="totalsSectionLabelColspan" class="text-right px-2">Total:</th>
                <td class="text-right px-2">{{ toCurrency(total(order)) }}</td>
                <td v-if="showHSTColumn" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            <tr v-if="type == 'invoice' && order.payment_status == 'paid' && order.moneris_receipt_amount > 0" class="not_striped">
                <th :colspan="totalsSectionLabelColspan" class="text-right px-2">Paid:</th>
                <td class="text-right px-2">{{ toCurrency(parseFloat(order.moneris_receipt_amount)) }}</td>
                <td v-if="showHSTColumn" class="w-10 max-w-[2.5rem]"></td>
            </tr>
            </template>
        </tbody>
    </table>
    <div v-if="showPricing && (type == 'outlet_order' && order.promo_code_amount > 0)" class="mt-3 p-3 rounded bg-gray-100 text-right">
        <div class="font-bold">Promo Code Discount:</div>
        <div class="text-gray-600">-{{ toCurrency(parseFloat(order.promo_code_amount)) }} applied</div>
    </div>
    </div>
</template>

<script>
import UseUserAuth from '@/Composables/UseUserAuth';
import { usePage } from '@inertiajs/inertia-vue3';
import { computeOrderTotals, getLineDiscountedTotal as getLineDiscountedTotalFn, lineDiscountAmount } from '@/Composables/useOrderTotals';

export default {
    components: {
    },

    setup () {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        return { isAdmin }
    },

    props: {
        order: { type: Object, required: true },
        type: { type: String, required: true },
    },

    computed: {
        showPricing() {
            if (this.order.gift) return false;
            if (this.type === 'invoice') return true;
            return !!(this.order.customer && this.order.customer.show_printed_order_pricing);
        },

        showHSTColumn() {
            return this.type === 'invoice'
                && (this.order.is_wholesale == 1 || this.order.customer?.type === 'School')
                && (this.tax(this.order) > 0 || this.tax(this.order) < 0);
        },

        orderAppliesInvoiceTax() {
            const o = this.order;
            if (o.tax_exempt === 1) {
                return false;
            }
            if (o.tax_exempt === null && o.customer && o.customer.tax_exempt) {
                return false;
            }
            return true;
        },

        // used for colspan on section headers
        invoiceDataColumnCount() {
            let n = 4;
            if (this.showPricing) {
                n += 2;
                if (this.hasPromo(this.order)) {
                    n += 1;
                }
            }
            if (this.showHSTColumn) {
                n += 1;
            }
            return n;
        },

        totalsSectionLabelColspan() {
            const n = this.invoiceDataColumnCount;
            if (this.showHSTColumn) {
                return Math.max(1, n - 2);
            }
            return Math.max(1, n - 1);
        },

        taxRowLabel() {
            return this.showHSTColumn ? 'HST:' : 'Tax:';
        },
    },

    methods: {
        subtotal (order) {
            return computeOrderTotals(order).subtotal;
        },
        tax(order) {
            return computeOrderTotals(order).tax;
        },
        total(order) {
            return computeOrderTotals(order).total;
        },
        delivery_fee(order) {
            return computeOrderTotals(order).deliveryFeeAfterDiscount;
        },

        hasPromo (order) {
            return !!(order.applied_promo_code || order.promo_code_id);
        },

        lineDiscountAmt (order, item) {
            return lineDiscountAmount(order, item);
        },

        getLineDiscountedTotal (order, item) {
            return getLineDiscountedTotalFn(order, item);
        },

        shown_subitems (order_item) {
            return order_item.subitems.filter(subitem => !order_item.product.subproducts.find(subproduct => subproduct.subproduct_id == subitem.product_id) || !order_item.product.subproducts.find(subproduct => subproduct.subproduct_id == subitem.product_id).hidden_from_customer)
        },

        orderItemsInlineSubitems(order_item)
        {
            if (order_item.product.include_subitems_on_picksheet && order_item.product.category.subitems_on_separate_picksheet == 0 && order_item.subitems.length > 0)
            {
                return this.orderItemsSortedByZone(this.shown_subitems(order_item));
            }
            return [];
        },

        orderItemsSortedByZone(order_items)
        {
            let zone_type = 'zone'
            let subzone_type = 'subzone'
            if(this.type === 'outlet_order'){
                zone_type = 'retail_zone'
                subzone_type = 'retail_subzone'
            }else if(this.order.customer?.type === 'School'){
                zone_type = 'school_zone'
                subzone_type = 'school_subzone'
            }

            return order_items.sort((a, b) => {
                if (a.product[zone_type] < b.product[zone_type]) return -1;
                else if (a.product[zone_type] > b.product[zone_type]) return 1;
                else {
                    if (a.product[subzone_type] < b.product[subzone_type]) return -1;
                    else if (a.product[subzone_type] > b.product[subzone_type]) return 1;
                    return 0;
                }
            });
        },

        orderItemsWithSubitemsOnSeparatePicksheet(order)
        {
            return order.order_items.filter(oi => (oi.product.include_subitems_on_picksheet && oi.product.category.subitems_on_separate_picksheet == 1 && this.shown_subitems(oi).length > 0));
        },

        isLastItemInZone (index) {
            let current_item = this.order.order_items[index];
            let next_item = this.order.order_items[index + 1];

            if (this.type == 'outlet_order') return next_item !== undefined && current_item.product.retail_zone != next_item.product.retail_zone
            else return next_item !== undefined && current_item.product.zone != next_item.product.zone
        },

        boldProductHighlights(string, wordsToHighlight = "") {
            if (this.type == 'invoice' || string == null) return string;
            let wordsToHighlightArr = []

            if (wordsToHighlight != null){
                wordsToHighlightArr = wordsToHighlight.split(',').map(w =>w.trim());
            }
            wordsToHighlightArr.push("case") //always hightlight "case"
            const regex = new RegExp(`\\b(${wordsToHighlightArr.join('|')})\\b`, 'gi');
            return string.replace(regex, match => `<b>${match}</b>`);
        },

        itemHSTChargeMarker(item) {
            if (!this.showHSTColumn || !this.orderAppliesInvoiceTax) {
                return '';
            }
            return item.tax === true ? 'HST' : '';
        },
    },
}

</script>
